import { Grid, Typography, useTheme } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import React, { useEffect, useRef, useState } from "react";
import { TitledDrawer } from "./TitledDrawer";
import {
    RenderedTranscription,
    TranscriptBySpeaker,
} from "models/LLMs/Testing/LLMExecutorExecutionResult";
import { Duration } from "components/UI/Duration";
import moment from "moment";
import { MIN_SEARCH_LENGTH } from "components/UI/ConversationSearch/ConversationSearch";
import { HighlightedText } from "./HighlightedText";
import { TranscriptSearch, TranscriptSearchResult } from "./TranscriptSearch";

interface ViewTranscriptProps {
    transcript: TranscriptBySpeaker[] | RenderedTranscription;
}

export function ViewTranscript({ transcript }: ViewTranscriptProps) {
    const theme = useTheme();
    const speakerColors = [
        theme.palette.primary.main,
        theme.palette.secondary.dark,
    ];
    const transcriptMoments = getTranscriptMoments(transcript);
    const speakerToColor = getSpeakerToColorMap(
        transcriptMoments,
        speakerColors,
    );

    const [isTranscriptDrawerOpen, setIsTranscriptDrawerOpen] = useState(false);
    const [searchString, setSearchString] = useState("");

    // Each highlighted occurrence has a drawer-scoped ref. Scrolling a whole
    // turn can leave the selected words offscreen in long turns.
    const matchRefs = useRef(new Map<string, HTMLSpanElement>());

    useEffect(() => {
        // Drop the query when the drawer closes or the transcript changes, so a
        // search never outlives the transcript it was run against.
        setSearchString("");
    }, [isTranscriptDrawerOpen, transcript]);

    // Below the threshold the search is inert, so the body must not highlight
    // either; otherwise a two-character query paints the transcript with
    // matches the results list doesn't acknowledge.
    const activeQuery =
        searchString.length >= MIN_SEARCH_LENGTH ? searchString : "";

    function scrollToResult(searchResult: TranscriptSearchResult) {
        matchRefs.current
            .get(`${searchResult.index}:${searchResult.matchStart}`)
            ?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
    }

    return (
        <React.Fragment>
            <AcxButton
                color="white"
                onClick={() => setIsTranscriptDrawerOpen(true)}
            >
                View Transcript
            </AcxButton>
            <TitledDrawer
                title="Transcript"
                open={isTranscriptDrawerOpen}
                onClose={() => setIsTranscriptDrawerOpen(false)}
                headerContent={
                    <TranscriptSearch
                        transcript={transcriptMoments}
                        searchString={searchString}
                        onSearchStringChange={setSearchString}
                        speakerToColor={speakerToColor}
                        onResultClick={scrollToResult}
                    />
                }
            >
                <TitledDrawer.Section pb={16}>
                    {transcriptMoments.map((transcriptMoment, index) => {
                        return (
                            <Grid
                                container
                                direction="column"
                                key={index}
                                flexWrap="nowrap"
                            >
                                <Grid
                                    container
                                    item
                                    columnGap={1}
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <Typography
                                            sx={{
                                                color: speakerToColor[
                                                    transcriptMoment.speaker
                                                ],
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {transcriptMoment.speaker}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            color="gray.600"
                                            fontSize="13px"
                                        >
                                            {transcriptMoment.startTime !=
                                                null && (
                                                <Duration
                                                    seconds={
                                                        transcriptMoment.startTime
                                                    }
                                                />
                                            )}
                                            {transcriptMoment.timestamp &&
                                                moment
                                                    .utc(
                                                        transcriptMoment.timestamp,
                                                    )
                                                    .local()
                                                    .format("HH:mm")}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Typography>
                                        <HighlightedText
                                            text={
                                                transcriptMoment.transcriptionText
                                            }
                                            query={activeQuery}
                                            matchRef={(start, element) => {
                                                const key = `${index}:${start}`;
                                                if (element)
                                                    matchRefs.current.set(
                                                        key,
                                                        element,
                                                    );
                                                else
                                                    matchRefs.current.delete(
                                                        key,
                                                    );
                                            }}
                                        />
                                    </Typography>
                                </Grid>
                            </Grid>
                        );
                    })}
                </TitledDrawer.Section>
            </TitledDrawer>
        </React.Fragment>
    );
}

function getSpeakerToColorMap(
    transcript: TranscriptBySpeaker[],
    availableColors: string[],
) {
    let colorIndex = 0;
    const colors: Record<string, string> = {};

    for (const transcriptMoment of transcript) {
        if (!colors[transcriptMoment.speaker]) {
            colors[transcriptMoment.speaker] =
                availableColors[colorIndex % availableColors.length];
            colorIndex++;
        }
    }

    return colors;
}

function getTranscriptMoments(
    transcript: TranscriptBySpeaker[] | RenderedTranscription,
): TranscriptBySpeaker[] {
    if (Array.isArray(transcript)) return transcript;
    if (!Array.isArray(transcript?.words)) return [];

    return transcript.words.reduce<TranscriptBySpeaker[]>((moments, word) => {
        const speaker = word.participantLabel || word.participantKey;
        const previousMoment = moments.at(-1);

        if (previousMoment?.speaker === speaker) {
            previousMoment.transcriptionText += ` ${word.value}`;
            previousMoment.endTime = word.endTime;
            return moments;
        }

        moments.push({
            speaker,
            transcriptionText: word.value,
            startTime: word.startTime,
            endTime: word.endTime,
            timestamp: word.messageTimestamp,
        });
        return moments;
    }, []);
}
